import { createTheme } from '@mui/material/styles'
import {
    Box,
    CssBaseline,
    Palette,
    PaletteColor,
    Theme,
    TypeText,
    alpha,
    darken,
    lighten,
} from '@mui/material'
import { logException, toStr } from '../services/sentryLogger'
import { fitmeColors } from '../parameters/defaultParamsFitme'
import { FullParameters } from '../parameters/types'

// All `Portal`-related components need to have the the main app wrapper element as a container
// so that the are in the subtree under the element used in the `important` option of the Tailwind's config.
// muiTheme is defaultly updated with the fitmeColors (cf. end of file)
export const muiTheme = createTheme({
    breakpoints: {
        // from tw-css https://tailwindcss.com/docs/screens
        values: {
            xs: 0,
            sm: 640, // was 600
            md: 768, // was 900
            lg: 1024, // was 1200
            xl: 1280, // was 1536
        },
    },
    typography: {
        fontFamily: ['"Open Sans", sans-serif'].join(','),
    },
})

// Usage :
// - import { useTheme } from '@mui/material'
// - const theme = useTheme();
// - <... color={theme.palette.primary.main} ...>
// TODO make sure base/common/unthemed colors are validated (green, red, black, white)
// ex. WarningMessage.tsx
type ColorPaletteType = keyof Pick<
    Theme['palette'],
    'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
>
export const updateThemeColor = (
    color: string,
    type: ColorPaletteType = 'primary'
): PaletteColor => {
    const { palette } = muiTheme
    const { text, background, grey } = palette // TODO
    const paletteColor: PaletteColor = palette[type]
    if (!color) {
        logException('updateThemeColor', 'empty color')
        return palette[type]
    }
    const newTheme = createTheme({
        palette: {
            [type]: {
                main: color,
            },
        },
    })
    paletteColor.main = color
    // Regened from main:
    console.debug('>>> updateThemeColor', type, toStr(newTheme.palette[type]))
    paletteColor.dark = newTheme.palette[type].dark // regen locally
    // TODO not light enough
    paletteColor.light = newTheme.palette[type].light // regen locally
    // TODO required? backgroundColor?
    // TODO will generate a black color for the fitme primary color ...
    // Keep default mui contrastText = '#ffffff' (white)
    // paletteColor.contrastText = newTheme.palette[type].contrastText

    return {
        main: paletteColor.main,
        dark: paletteColor.dark,
        light: paletteColor.light,
        contrastText: paletteColor.contrastText,
    }
}

export const updateBackgroundColor = (newColor: string) => {
    const { palette } = muiTheme
    const { background } = palette

    // Paper is typically a slightly darker shade of the default background
    const paperColor = darken(newColor, 0.05) // 5% darker

    background.default = newColor
    background.paper = paperColor

    console.debug('updateBackgroundColor', toStr(background))
    return {
        default: newColor,
        paper: paperColor,
    }
}

export const updateGreyColor = (newColor: string) => {
    const { palette } = muiTheme
    const { grey } = palette

    // Generate all shades from the base color
    const shades = {
        '50': lighten(newColor, 0.95),
        '100': lighten(newColor, 0.9),
        '200': lighten(newColor, 0.8),
        '300': lighten(newColor, 0.7),
        '400': lighten(newColor, 0.5),
        '500': newColor, // Base color
        '600': darken(newColor, 0.1),
        '700': darken(newColor, 0.2),
        '800': darken(newColor, 0.3),
        '900': darken(newColor, 0.4),
        A100: lighten(newColor, 0.8),
        A200: lighten(newColor, 0.6),
        A400: lighten(newColor, 0.4),
        A700: lighten(newColor, 0.2),
    }

    // Update the grey palette
    Object.assign(grey, shades)
    return shades
}

export const updateTextColor = (color: string): TypeText => {
    if (!color) {
        logException('updateThemeColor', 'empty color')
        return {
            primary: muiTheme.palette.text.primary,
            secondary: muiTheme.palette.text.secondary,
            disabled: muiTheme.palette.text.disabled,
            // 'hint': muiTheme.palette.text.hint,
        }
    }

    muiTheme.palette.text.primary = color
    // Generate secondary and disabled colors from primary
    // FIXME cannot gen them via createTheme({palette: {text: {primary: color}}})
    muiTheme.palette.text.secondary = alpha(color, 0.7)
    muiTheme.palette.text.disabled = alpha(color, 0.38)

    return {
        primary: color,
        secondary: alpha(color, 0.7),
        disabled: alpha(color, 0.38),
    }
}

export const updateCommonColor = (light: string, dark: string) => {
    const { palette } = muiTheme
    const { common } = palette
    common.black = light
    common.white = dark
}

export type InputColors = Required<
    Pick<
        FullParameters['brandParams'],
        'primaryColor' | 'secondaryColor' | 'textColor' | 'backgroundColor'
    >
>

/**
 * TODO clean the rest
 */
export const updateWholeTheme = (args: InputColors) => {
    const { primaryColor, secondaryColor, textColor, backgroundColor } = args
    const primaryGen = updateThemeColor(primaryColor, 'primary')
    const secondaryGen = updateThemeColor(secondaryColor, 'secondary')
    const textColorGen = updateTextColor(textColor)
    const greyGen = updateGreyColor(alpha(textColor, 0.5))
    const backgroundGen = updateBackgroundColor(backgroundColor)
    const commonGen = updateCommonColor(backgroundColor, textColor)
    const output = {
        primaryGen,
        secondaryGen,
        textColorGen,
        greyGen,
        backgroundGen,
        commonGen,
    }
    // TODO remove
    // error 'text-[#ff3030]'
    // bg-[#ff3838]
    // warning 'text-[#ff9800]'
    // success 'text-[#5dc16e]'
    // info 'text-[#0288d1]'
    // disabled 'text-[#000000]'
    // 'text-[#4B5563]'
    // hint 'text-[#000000]'
    // primary 'text-[#000000]'
    // secondary 'text-[#000000]'
    console.debug(
        'updateWholeTheme',
        toStr({
            output,
            palette: muiTheme.palette,
        })
    )
    return output
}

updateWholeTheme(fitmeColors)
